import { ClientTransferRequestId, daysAgo } from "@sp-crm/core";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Spinner } from "components/ui/spinner";
import {
    GetPendingOutgoingClientTransfersQuery,
    useCancelClientTransferMutation,
    useResendClientTransferMutation,
} from "generated/graphql";
import * as React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Actions } from "store/actions";
import { GlobalSuccessNotificationCreate } from "store/actions/global-notifications";

interface Props {
    clientTransferRequest: GetPendingOutgoingClientTransfersQuery["getPendingOutgoingClientTransfers"][number];
    onCancelled: (clientTransferRequestId: ClientTransferRequestId) => void;
}

export const OutgoingTransferCard: React.FunctionComponent<Props> = (props: Props) => {
    const { clientTransferRequest, onCancelled } = props;
    const hasBothBestContactNameAndName =
        clientTransferRequest.client.bestContactName && clientTransferRequest.client.name;

    const globalDispatch = useDispatch();

    const cancelTransfer = useCancelClientTransferMutation();

    const onCancelTransfer = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();

            const confirm = await fancyConfirm(
                "Cancel transfer?",
                "This will cancel the transfer request. The client will not be transferred.",
                "Yes, cancel",
                "No, go back",
            );

            if (!confirm) {
                return;
            }

            await cancelTransfer.mutateAsync({
                clientTransferRequestId: clientTransferRequest.id,
            });
            onCancelled(clientTransferRequest.id);
        },
        [cancelTransfer, onCancelled, clientTransferRequest.id],
    );

    const resendTransfer = useResendClientTransferMutation();

    const onResendTransfer = React.useCallback(async () => {
        await resendTransfer.mutateAsync({
            clientTransferRequestId: clientTransferRequest.id,
        });
        const successNotification: GlobalSuccessNotificationCreate = {
            messageId: "resend-client-transfer",
            messageTitle: "Sent!",
            messageBody: "Client transfer notification sent.",
            type: Actions[Actions.GLOBAL_SUCCESS_NOTIFY],
        };
        globalDispatch(successNotification);
    }, [resendTransfer, clientTransferRequest.id, globalDispatch]);

    return (
        <div className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow">
            <div className="flex-1 overflow-auto p-4">
                <div className="flex space-x-2">
                    <div className="flex-1">
                        <Link
                            to={`/clients/show/${clientTransferRequest.client.id}`}
                            className="twoverride text-gray-900 text-lg">
                            {hasBothBestContactNameAndName ? (
                                <div>
                                    {clientTransferRequest.client.bestContactName}
                                    <br />
                                    {clientTransferRequest.client.name}
                                </div>
                            ) : (
                                clientTransferRequest.client.name ??
                                clientTransferRequest.client.bestContactName
                            )}
                        </Link>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-auto">
                <ul className="p-4 space-y-2">
                    <li>
                        Sent by:{" "}
                        {clientTransferRequest.createdByEntity.name ??
                            clientTransferRequest.createdByEntity.email}
                    </li>
                    <li>
                        To:{" "}
                        {clientTransferRequest.toUserProfile.name ??
                            clientTransferRequest.toUserProfile.email}
                    </li>
                    <li>
                        Last email sent {daysAgo(clientTransferRequest.lastEmailSentAt)}
                    </li>
                </ul>
            </div>
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={onCancelTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-red-600 bg-red-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-bl-lg">
                        <span className="ml-3">Cancel transfer</span>
                        {cancelTransfer.isLoading ? <Spinner /> : null}
                    </a>
                    <a
                        onClick={onResendTransfer}
                        className="relative inline-flex w-0 flex-1 items-center justify-center border border-transparent py-4 text-sm font-medium text-brand-600 bg-brand-100 hover:bg-gray-100 hover:text-gray-800 twoverride rounded-br-lg">
                        <span className="ml-3">Resend</span>
                        {resendTransfer.isLoading ? <Spinner /> : null}
                    </a>
                </div>
            </div>
        </div>
    );
};
